import {
  getPatientSummaryFullName,
  patientInitials,
} from '@/api/mappers';
import { formatPartialDate } from '@/lib/format';
import type { PatientSummaryViewModel } from '@/models/patient';

import type { QuickSearchResult } from './QuickSearch';

/**
 * Adapts a `PatientSummaryViewModel` to QuickSearch's row-shape. Lives
 * next to `QuickSearch` (not in `mappers/`) because the target shape is
 * a UI-component contract, not a server contract — keeping the
 * view-model UI-agnostic and `QuickSearch` data-agnostic.
 *
 * Pure function, no React surface, so it's trivially unit-testable and
 * reusable by any future surface that feeds QuickSearch (e.g., a
 * "recent patients" fallback or keyboard-shortcut launcher).
 */
export function toQuickSearchResult(
  summary: PatientSummaryViewModel,
): QuickSearchResult {
  const fullName = getPatientSummaryFullName(summary) ?? summary.guid;
  const sublabelParts = [
    summary.dateOfBirth !== null ? formatPartialDate(summary.dateOfBirth) : null,
    summary.program,
  ].filter((part): part is string => part !== null && part.length > 0);
  return {
    id: summary.guid,
    label: fullName,
    sublabel: sublabelParts.join(' · '),
    initials: patientInitials(summary.firstName, summary.lastName),
    href: `/patients/${summary.guid}`,
    sensitive: summary.sensitive,
  };
}
